import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, Check, ChevronDown, Search } from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { cn } from '@/lib/utils';

/**
 * Empresa — a primeira escolha do contexto, antes do centro.
 *
 * Por que voltou a ser um seletor próprio, se antes empresa e centro eram um
 * botão só: porque empresa e centro respondem a perguntas diferentes. "Qual
 * empresa eu estou operando" é uma decisão de enquadramento, que muda o que a
 * lista de centros sequer oferece; "qual CD" é uma decisão dentro dela. Com os
 * dois no mesmo painel, quem tem 28 empresas e 200 centros rolava uma lista
 * única atrás do CD certo.
 *
 * O rótulo é sempre `id · nome`, e não só o nome, por dois motivos: é assim que
 * a operação fala ("a 50", "a 11"), e `NOME_FANTASIA` no ERP NÃO é único —
 * "MEGAMAMUTE" aparece em quatro empresas diferentes (9, 10, 12, 32). Sem o
 * número, duas linhas do menu ficariam idênticas.
 *
 * Com uma empresa só, vira etiqueta: não se pede clique para uma escolha que
 * não existe.
 */
export function SeletorEmpresa(): JSX.Element | null {
  const { empresas, empresa, empresaAtual, definirEmpresa, carregando } = useContexto();
  const [aberto, definirAberto] = React.useState(false);
  const [termo, definirTermo] = React.useState('');

  const filtradas = React.useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return empresas;
    return empresas.filter(
      (e) => String(e.empresa).includes(t) || (e.nome ?? '').toLowerCase().includes(t),
    );
  }, [empresas, termo]);

  if (carregando) return <Esqueleto className="h-11 w-44" />;
  if (empresas.length === 0 || empresa === null) return null;

  const rotulo = (
    <span className="min-w-0 text-left leading-tight">
      <span className="block text-[9.5px] font-bold uppercase tracking-[0.1em] text-textoSuave">
        Empresa
      </span>
      <span className="block truncate text-[13px] font-semibold text-texto">
        <span className="tabular">{empresa}</span>
        {empresaAtual?.nome ? ` · ${empresaAtual.nome}` : ''}
      </span>
    </span>
  );

  const icone = (
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primaria-suave text-primaria">
      <Building2 className="h-4 w-4" />
    </span>
  );

  // Uma empresa só: mostra, não pergunta.
  if (empresas.length === 1) {
    return (
      <span className="flex min-h-[42px] max-w-[240px] items-center gap-2.5 rounded-xl border border-borda bg-superficie2 px-2.5 pr-3">
        {icone}
        {rotulo}
      </span>
    );
  }

  return (
    <DropdownMenu.Root
      open={aberto}
      onOpenChange={(v) => {
        definirAberto(v);
        if (!v) definirTermo('');
      }}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Trocar de empresa"
          className={cn(
            'group flex min-h-[42px] max-w-[260px] items-center gap-2.5 rounded-xl border border-borda bg-superficie px-2.5 pr-3 shadow-xs transition-all',
            'hover:border-bordaForte hover:shadow-cartao data-[state=open]:border-primaria data-[state=open]:ring-4 data-[state=open]:ring-primaria/10',
          )}
        >
          {icone}
          {rotulo}
          <ChevronDown className="h-4 w-4 shrink-0 text-textoSuave transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className="z-50 w-[min(340px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar"
        >
          {/* Busca só quando a lista passa do que se varre com o olho. */}
          {empresas.length > 7 ? (
            <div className="flex items-center gap-2 border-b border-borda px-3">
              <Search className="h-4 w-4 shrink-0 text-textoSuave" />
              <input
                autoFocus
                value={termo}
                onChange={(e) => definirTermo(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Buscar empresa…"
                aria-label="Buscar empresa"
                className="min-h-[46px] w-full bg-transparent text-[13.5px] outline-none placeholder:text-textoSuave"
              />
            </div>
          ) : null}

          <div className="max-h-[52vh] overflow-y-auto p-1.5">
            {filtradas.length === 0 ? (
              <p className="px-3 py-6 text-center text-[13px] text-textoFraco">
                Nenhuma empresa com “{termo}”.
              </p>
            ) : (
              filtradas.map((e) => {
                const ativa = e.empresa === empresa;
                return (
                  <button
                    key={e.empresa}
                    type="button"
                    onClick={() => {
                      definirEmpresa(e.empresa);
                      definirAberto(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors',
                      ativa ? 'bg-primaria-suave' : 'hover:bg-superficie2',
                    )}
                  >
                    <span
                      className={cn(
                        'tabular grid h-9 w-11 shrink-0 place-items-center rounded-lg text-[12px] font-bold',
                        ativa
                          ? 'gradiente-marca text-white'
                          : 'bg-superficie2 text-textoFraco ring-1 ring-inset ring-borda',
                      )}
                    >
                      {e.empresa}
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">
                      <span
                        className={cn(
                          'block truncate text-[13px] font-semibold',
                          ativa && 'text-primaria',
                        )}
                      >
                        {e.nome ?? `Empresa ${e.empresa}`}
                      </span>
                      <span className="block text-[11px] text-textoFraco">
                        {e.centros} {e.centros === 1 ? 'centro' : 'centros'}
                      </span>
                    </span>
                    {ativa ? <Check className="h-4 w-4 shrink-0 text-primaria" /> : null}
                  </button>
                );
              })
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
